import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Chip,
  Button,
  Stack,
  Snackbar,
  Alert,
} from '@mui/material';
import { fetchCustomPhrases, updateCustomPhrases } from '../../utils/axiosApiRequest';
import { useTranslation } from 'react-i18next';

const KeywordManager = () => {
  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState('');
  const [dirty, setDirty] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    fetchCustomPhrases().then(setKeywords);
  }, []);

  const addKeyword = () => {
    const trimmed = newKeyword.trim();
    if (!trimmed || keywords.find((kw) => kw.phrase === trimmed)) return;

    setKeywords((prev) => [...prev, { phrase: trimmed }]);
    setNewKeyword('');
    setDirty(true);
  };

  const removeKeyword = (idOrPhrase) => {
    setKeywords((prev) =>
      prev.filter((kw) => kw.id !== idOrPhrase && kw.phrase !== idOrPhrase)
    );
    setDirty(true);
  };

  const saveKeywords = () => {
    const phrases = keywords.map((kw) => kw.phrase); // extract just the string values
    updateCustomPhrases(phrases)
        .then((updated) => {
        setKeywords(updated); // updated is the full [{ id, phrase }, ...]
        setDirty(false);
        setFeedback({ type: 'success', message: t('settings.keywordsSaved') });
        })
        .catch(() => {
        setFeedback({ type: 'error', message: t('settings.keywordsError') });
        });
    };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom>
        {t('settings.keywordsTitle')}
      </Typography>
        <Box sx={{ mt: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {keywords.map((kw) => (
            <Chip
            key={kw.id ?? kw.phrase}
            label={kw.phrase}
            onDelete={() => removeKeyword(kw.id ?? kw.phrase)}
            />
        ))}
        </Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          label="New keyword"
          value={newKeyword}
          onChange={(e) => setNewKeyword(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addKeyword()}
          size="small"
        />
        <Button variant="contained" onClick={addKeyword}>
          {t('common.add')}
        </Button>
        {dirty && (
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" color="primary" onClick={saveKeywords}>
            {t('settings.saveKeywords')}
          </Button>
        </Box>
      )}
      </Stack>

      <Snackbar
        open={!!feedback}
        autoHideDuration={3000}
        onClose={() => setFeedback(null)}
      >
        <Alert
          severity={feedback?.type}
          sx={{ width: '100%' }}
          onClose={() => setFeedback(null)}
        >
          {feedback?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default KeywordManager;
